import React from 'react';
import { Typography, Stack, Box, Link, Button } from '@mui/material';
import { OpenInNew, Upload } from '@mui/icons-material';
import ItemImageBox from '../../Inventory/ItemImageBox';

function ItemImageReviewDetailPage({ 
    item, 
    itemChanges, 
    handleValidateImage, 
    handleRejectImage,
    handleUploadImage,
    rowIndex,
    table,
    autoCollapsedRows,
    setAutoCollapsedRows 
}) {
    // Filter out rejected images and reorder validated images
    let processedImages = item.images ?? [];
    
    // Remove rejected images
    if (itemChanges.rejectedImages) {
        processedImages = processedImages.filter(img => !itemChanges.rejectedImages.includes(img.id));
    }
    
    // Update validation status for locally validated images
    processedImages = processedImages.map(img => {
        if (itemChanges.validatedImages?.includes(img.id)) {
            return { ...img, is_manually_validated: 1 };
        }
        return img;
    });

    // Determine if we should auto-collapse this row
    const newValidatedImages = itemChanges.validatedImages || [];
    const newRejectedImages = itemChanges.rejectedImages || [];
    const areAllImagesLoadedValidated = item.images.every(img => img.is_manually_validated);
    const areAllImagesProcessed = item.images.every(img =>
        newValidatedImages.includes(img.id) 
        || newRejectedImages.includes(img.id)
        || img.is_manually_validated
    );

    if (areAllImagesLoadedValidated && !autoCollapsedRows.has(item.id)) {
        // If all images are loaded and processed, we can auto-collapse the row
        setAutoCollapsedRows(prev => new Set([...prev, item.id]));
    }
    else if (areAllImagesProcessed && table && rowIndex !== undefined && !autoCollapsedRows.has(item.id)) {
        // Mark this row as auto-collapsed and highlighted
        setAutoCollapsedRows(prev => new Set([...prev, item.id]));
        
        table.setExpanded(prev => ({
            ...prev,
            [rowIndex]: false
        }));
    }
    
    // Sort remaining images: unvalidated first, then validated
    processedImages.sort((a, b) => {
        const aValidated = a.is_manually_validated || 0;
        const bValidated = b.is_manually_validated || 0;
        return aValidated - bValidated;
    });

    return (
        <Box sx={{ padding: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Typography variant="h5">
                    Images for Validation
                </Typography>
                <Link 
                    href={`${window.location.origin}/inventory/${item.id}/details`}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                    Item Details
                    <OpenInNew fontSize="small" />
                </Link>
            </Box>
            <Stack spacing={1}>
                <Typography>
                    <strong>Name:</strong> {item.name}
                </Typography>
                {item.external_id && (
                    <Typography>
                        <strong>External ID:</strong> {item.external_id}
                    </Typography>
                )}
                {item.description && (
                    <Typography>
                        <strong>Description:</strong> {item.description}
                    </Typography>
                )}
                {item.vendor && item.vendor.name && (
                    <Typography>
                        <strong>Vendor Name:</strong> {item.vendor.name}
                    </Typography>
                )}
                {item.part_number && (
                    <Typography>
                        <strong>Vendor Part Number:</strong> {item.part_number}
                    </Typography>
                )}
                {processedImages && processedImages.length > 0 && (
                    <Box>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="subtitle2">
                                <strong>Images:</strong>
                            </Typography>
                            <Button 
                                variant="outlined"
                                size="small"
                                startIcon={<Upload />}
                                onClick={handleUploadImage}
                            >
                                Upload Image
                            </Button>
                        </Stack>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            {processedImages.slice(0, 3).map((image) => {
                                const isLocallyValidated = itemChanges.validatedImages?.includes(image.id);
                                const isLocallyRejected = itemChanges.rejectedImages?.includes(image.id);
                                const forceRenderKey = `${image.id}-${image.is_manually_validated}-${isLocallyValidated}-${isLocallyRejected}-${itemChanges.timestamp || 0}`;
                                
                                return (
                                    <ItemImageBox 
                                        key={forceRenderKey}
                                        item={item} 
                                        image={image} 
                                        canEdit={true} 
                                        validateItemImage={handleValidateImage} 
                                        deleteItemImage={handleRejectImage} 
                                    />
                                );
                            })}
                            {processedImages.length > 3 && (
                                <Typography variant="body2" sx={{ alignSelf: 'center', ml: 1 }}>
                                    +{processedImages.length - 3} more
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                )}
                {(!processedImages || processedImages.length === 0) && (
                    <Box>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Typography variant="subtitle2">
                                <strong>Images:</strong> No images available
                            </Typography>
                            <Button 
                                variant="contained"
                                size="small"
                                startIcon={<Upload />}
                                onClick={handleUploadImage}
                            >
                                Upload Image
                            </Button>
                        </Stack>
                    </Box>
                )}
            </Stack>
        </Box>
    );
}

export default ItemImageReviewDetailPage;